import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, FileDown, Ban, RefreshCw, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_payment: "In attesa pagamento",
  active: "Attivo",
  scheduled: "Programmato",
  redeemed: "Usato",
  expired: "Scaduto",
  cancelled: "Annullato",
};

const STATUS_COLOR = {
  pending_payment: "bg-amber-100 text-amber-800",
  active: "bg-green-100 text-green-800",
  scheduled: "bg-sky-100 text-sky-800",
  redeemed: "bg-[#0D253A]/10 text-[#0D253A]",
  expired: "bg-neutral-200 text-neutral-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const AdminGiftCards = ({ authHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gift-cards`, { headers: authHeaders });
      setItems(data.cards || []);
    } catch { toast.error("Impossibile caricare i buoni"); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const resend = async (row) => {
    try {
      await axios.post(`${API}/admin/gift-cards/${row.id}/resend`, {}, { headers: authHeaders });
      toast.success("Email reinviata al destinatario");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reinvio fallito"); }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Annullare il buono ${row.code}? (rimborso manuale)`)) return;
    try {
      await axios.patch(`${API}/admin/gift-cards/${row.id}`, { status: "cancelled" }, { headers: authHeaders });
      load();
    } catch { toast.error("Annullamento fallito"); }
  };

  const filtered = items.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (r.code || "").toLowerCase().includes(s) ||
             (r.recipient_email || "").toLowerCase().includes(s) ||
             (r.buyer_email || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="studio-gift-cards" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-[#0D253A]/10 p-4">
        <Filter className="w-4 h-4 text-[#5C6773]" />
        <select data-testid="gift-filter-status" value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 rounded-lg bg-white border border-[#0D253A]/15 text-sm">
          <option value="all">Tutti</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input data-testid="gift-filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Codice / email…" className="flex-1 min-w-[180px] px-3 py-2 rounded-lg bg-white border border-[#0D253A]/15 text-sm" />
        <button onClick={load} className="p-2 rounded-full border border-[#0D253A]/20 text-[#0D253A]/70 hover:text-[#C86D51]" aria-label="Aggiorna">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C6773] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</p>
      ) : filtered.length === 0 ? (
        <p data-testid="gift-empty" className="text-sm text-[#5C6773]">Nessun buono per questo filtro.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => (
            <div key={row.id} data-testid={`gift-row-${row.code}`} className="bg-white rounded-2xl border border-[#0D253A]/10 p-4 flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-[#0D253A]">
                  <span className="font-mono text-sm">{row.code}</span>
                  <span className="mx-3 text-[#5C6773]">·</span>
                  € {Number(row.amount_initial).toFixed(2)}
                  <span className="text-xs text-[#5C6773] font-normal"> (residuo € {Number(row.amount_remaining || 0).toFixed(2)})</span>
                </p>
                <p className="text-xs text-[#5C6773] mt-1">
                  Da <strong>{row.buyer_name}</strong> ({row.buyer_email}) → per <strong>{row.recipient_name}</strong> ({row.recipient_email})
                </p>
                <p className="text-xs text-[#5C6773] mt-0.5">
                  Emesso: {row.created_at?.slice(0, 10) || "-"}
                  {row.delivery_date ? ` · Invio programmato: ${row.delivery_date}` : row.sent_at ? ` · Inviato: ${row.sent_at.slice(0, 10)}` : ""}
                  {row.expires_at ? ` · Scade: ${row.expires_at.slice(0, 10)}` : ""}
                </p>
                {row.message && <p className="text-xs italic text-[#5C6773] mt-1">"{row.message}"</p>}
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${STATUS_COLOR[row.status] || "bg-neutral-200 text-neutral-700"}`}>
                  {STATUS_LABEL[row.status] || row.status}
                </span>
                <div className="flex items-center gap-1.5">
                  {(row.status === "active" || row.status === "scheduled" || row.status === "redeemed") && (
                    <button data-testid={`gift-resend-${row.code}`} onClick={() => resend(row)} title="Reinvia email al destinatario"
                      className="p-2 rounded-full border border-[#0D253A]/20 hover:border-[#C86D51] hover:text-[#C86D51]">
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                  <a data-testid={`gift-pdf-${row.code}`} href={`${API}/gift-cards/pdf/${row.code}`} target="_blank" rel="noopener noreferrer"
                    title="Scarica PDF" className="p-2 rounded-full border border-[#0D253A]/20 hover:border-[#C86D51] hover:text-[#C86D51]">
                    <FileDown className="w-4 h-4" />
                  </a>
                  {row.status !== "cancelled" && row.status !== "redeemed" && (
                    <button data-testid={`gift-cancel-${row.code}`} onClick={() => cancel(row)} title="Annulla"
                      className="p-2 rounded-full border border-[#0D253A]/20 hover:border-[#C86D51] hover:text-[#C86D51]">
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGiftCards;
